import type { CallLog, CrmDialContactRow, DialPadSuggestion, OperationsUser } from './types';

const MAX_SUGGESTIONS = 12;
const MIN_QUERY_DIGITS = 2;

export function onlyDigits(s: string): string {
  return s.replace(/\D/g, '');
}

function matchScore(q: string, phoneDigits: string): number {
  if (!q || !phoneDigits) return 0;
  if (!phoneDigits.includes(q)) return 0;
  if (phoneDigits === q) return 100;
  if (phoneDigits.endsWith(q)) return 85;
  if (phoneDigits.startsWith(q)) return 75;
  return 55;
}

function displayNameCrm(c: CrmDialContactRow): string {
  if (c.first_name || c.last_name) {
    return `${c.first_name || ''} ${c.last_name || ''}`.trim();
  }
  return c.name || c.email || 'Contact';
}

type TeamWithPhone = { id: string; name: string; phone: string };

function teamContactsWithPhones(users: OperationsUser[]): TeamWithPhone[] {
  const out: TeamWithPhone[] = [];
  for (const u of users) {
    const ext = u as OperationsUser & { phone?: string; mobile?: string };
    const phone = ext.mobile?.trim() || ext.phone?.trim();
    if (!phone) continue;
    const pd = onlyDigits(phone);
    if (pd.length < 3) continue;
    out.push({
      id: `team-${u.id}-${pd}`,
      name: `${u.first_name} ${u.last_name}`.trim() || u.email,
      phone,
    });
  }
  return out;
}

function primaryAndSecondary(
  c: CrmDialContactRow,
): { primary: string; secondary: string | null } {
  const mobile = c.mobile?.trim();
  const phone = c.phone?.trim();
  if (mobile && phone && mobile !== phone) {
    return { primary: mobile, secondary: phone };
  }
  return { primary: mobile || phone || '', secondary: null };
}

export function dialPhoneFromCallLog(log: CallLog): { number: string; name: string } | null {
  const isSip =
    !!log.is_sip_call || log.call_type === 'sip_inbound' || log.call_type === 'sip_outbound';

  if (isSip) {
    const isOutgoing =
      log.direction === 'outbound' || log.call_type === 'sip_outbound';
    const num = (isOutgoing ? log.target_number : log.caller_number)?.trim();
    if (!num || onlyDigits(num).length < 2) return null;
    const name = isOutgoing
      ? log.target_user_name || log.target_number || num
      : log.caller_name || log.caller_number || num;
    return { number: num, name };
  }

  const num = (log.target_number || log.caller_number)?.trim();
  if (!num || onlyDigits(num).length < 3) return null;
  const name = log.target_user_name || log.caller_name || num;
  return { number: num, name };
}

export function buildDialPadSuggestions(
  dialQuery: string,
  options: {
    operationsUsers: OperationsUser[];
    crmRows: CrmDialContactRow[];
    callLogs: CallLog[];
  },
): DialPadSuggestion[] {
  const q = onlyDigits(dialQuery);
  if (q.length < MIN_QUERY_DIGITS) return [];

  const candidates: DialPadSuggestion[] = [];

  for (const t of teamContactsWithPhones(options.operationsUsers)) {
    const pd = onlyDigits(t.phone);
    const score = matchScore(q, pd);
    if (score <= 0) continue;
    candidates.push({
      id: t.id,
      displayName: t.name,
      phoneDisplay: t.phone,
      dialString: t.phone,
      phoneDigits: pd,
      source: 'team',
      score: score + 3,
    });
  }

  for (const c of options.crmRows) {
    const { primary, secondary } = primaryAndSecondary(c);
    if (!primary) continue;
    const name = displayNameCrm(c);
    const labelBase =
      [name, c.company].filter(Boolean).join(' · ') || name;

    for (const phone of secondary ? [primary, secondary] : [primary]) {
      const pd = onlyDigits(phone);
      if (pd.length < 2) continue;
      const score = matchScore(q, pd);
      if (score <= 0) continue;
      candidates.push({
        id: `crm-${c.id}-${pd}`,
        displayName: labelBase,
        phoneDisplay: phone,
        dialString: phone,
        phoneDigits: pd,
        source: 'crm',
        score: score + 1,
      });
    }
  }

  const seenRecentDigits = new Set<string>();
  const sortedLogs = [...options.callLogs].sort(
    (a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime(),
  );
  for (const log of sortedLogs) {
    const parsed = dialPhoneFromCallLog(log);
    if (!parsed) continue;
    const pd = onlyDigits(parsed.number);
    if (seenRecentDigits.has(pd)) continue;
    const score = matchScore(q, pd);
    if (score <= 0) continue;
    seenRecentDigits.add(pd);

    const ageMs = Date.now() - new Date(log.started_at).getTime();
    const days = ageMs / (24 * 60 * 60 * 1000);
    const recencyBoost = Math.max(0, 12 - Math.min(12, days));

    candidates.push({
      id: `recent-${log._id}-${pd}`,
      displayName: parsed.name,
      phoneDisplay: parsed.number,
      dialString: parsed.number,
      phoneDigits: pd,
      source: 'recent',
      score: score + 4 + recencyBoost,
    });
  }

  candidates.sort((a, b) => b.score - a.score);

  const bestByDigits = new Map<string, DialPadSuggestion>();
  for (const c of candidates) {
    if (!bestByDigits.has(c.phoneDigits)) {
      bestByDigits.set(c.phoneDigits, c);
    }
  }

  return Array.from(bestByDigits.values())
    .sort((a, b) => b.score - a.score)
    .slice(0, MAX_SUGGESTIONS);
}
